import {
    FormInputGroup,
    FormMultiSelect,
    FormText,
    FormSection,
} from 'components/form';
import {
    FormRadio,
    FormRadioOption,
} from 'components/form/FormRadio/FormRadio';
import { PeriodicUsageAlert } from 'components/Devices/PeriodicUsageAlert/PeriodicUsageAlert';
import { includesPeriodicUsage } from 'helpers/device';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormState } from 'hooks/useFormState';
import {
    DeviceConsumptionType,
    DeviceFuelType,
    DeviceGaugeType,
    DeviceOrientationType,
    SharedDeviceInput,
} from 'types';
import { useLazyPatchUpdateDeviceQuery } from 'store/services';
import { useFormContext } from 'react-hook-form';

export interface CreateDeviceModalDetailsSectionProps {
    deviceId?: string;
    /** Whether or not this for a virtual device. */
    isVirtual?: boolean;
}

export function CreateDeviceModalDetailsSection({
    deviceId,
    isVirtual = false,
}: CreateDeviceModalDetailsSectionProps) {
    const formSectionTitle = 'Details';
    const [isPeriodicUsage, setIsPeriodicUsage] = useState(false);
    const { watch, setValue } = useFormContext<SharedDeviceInput>();

    const { currentFormSectionIndex, formSections } = useFormState();

    const [isDetailsFormSelected, setIsDetailsFormSelected] = useState(false);

    // Manage the submit button state
    const { setIsFormSubmitDisabled } = useFormState();
    useEffect(() => {
        if (
            currentFormSectionIndex === formSections.indexOf(formSectionTitle)
        ) {
            setIsFormSubmitDisabled(false);
        }
    }, [setIsFormSubmitDisabled, currentFormSectionIndex, formSections]);

    // Get the device update query
    const [deviceUpdateQuery] = useLazyPatchUpdateDeviceQuery();

    const orientation = watch('orientation');
    const fuelType = watch('fuelType');
    const gaugeType = watch('gaugeType');
    const consumptionTypes = watch('consumptionType');
    const capacity = watch('capacity');

    useEffect(() => {
        if (fuelType !== 'oil') {
            setValue('gaugeType', '');
        }
    }, [fuelType, setValue]);

    const shouldUpdateDevice = useMemo(() => {
        return !isVirtual && fuelType && orientation && deviceId;
    }, [isVirtual, fuelType, orientation, deviceId]);

    const runUpdateDeviceQueryCallback = useCallback(async () => {
        if (deviceId && !isVirtual) {
            await deviceUpdateQuery({
                deviceId: deviceId,
                updatedFields: {
                    fuelType,
                    orientation,
                    gaugeType,
                    capacity,
                },
            });
        }
    }, [
        deviceId,
        isVirtual,
        fuelType,
        orientation,
        gaugeType,
        deviceUpdateQuery,
        capacity,
    ]);

    // If we navigate away from details section, run update device query.
    useEffect(() => {
        const detailsSectionSelected =
            currentFormSectionIndex === formSections.indexOf(formSectionTitle);
        setIsDetailsFormSelected(detailsSectionSelected);

        const shouldExecuteOnCloseHandlers =
            isDetailsFormSelected && !detailsSectionSelected;

        if (shouldExecuteOnCloseHandlers && shouldUpdateDevice) {
            runUpdateDeviceQueryCallback();
        }
    }, [
        isDetailsFormSelected,
        shouldUpdateDevice,
        runUpdateDeviceQueryCallback,
        currentFormSectionIndex,
        formSections,
    ]);

    // Determine whether or not to show periodic usage alert.
    useEffect(() => {
        setIsPeriodicUsage(includesPeriodicUsage(fuelType, consumptionTypes));
    }, [consumptionTypes, fuelType]);

    return (
        <FormSection title={formSectionTitle}>
            <FormInputGroup>
                <FormRadio
                    field="fuelType"
                    options={fuelTypeOptions}
                    horizontal={true}
                />
                <FormRadio
                    field="orientation"
                    options={orientationOptions}
                    horizontal={true}
                />
            </FormInputGroup>
            <FormInputGroup>
                <FormText field="capacity" label="Capacity (Gal.)" isNumber />
                {fuelType === 'oil' && (
                    <FormRadio
                        field="gaugeType"
                        options={gaugeTypeOptions}
                        horizontal={true}
                    />
                )}
            </FormInputGroup>
            <FormInputGroup>
                <FormMultiSelect
                    field="consumptionType"
                    options={consumptionTypeOptions}
                />
                <FormText field="delivery_instructions" isTextArea />
            </FormInputGroup>
            {isVirtual && isPeriodicUsage && <PeriodicUsageAlert />}
        </FormSection>
    );
}

export const gaugeTypeOptions: FormRadioOption<DeviceGaugeType>[] = [
    { value: 'spiral', name: 'Spiral' },
    { value: 'swing_arm', name: 'Swing Arm' },
];

export const fuelTypeOptions: FormRadioOption<DeviceFuelType>[] = [
    { value: 'propane', name: 'Propane' },
    { value: 'oil', name: 'Oil' },
];

export const orientationOptions: FormRadioOption<DeviceOrientationType>[] = [
    { value: 'horizontal', name: 'Horizontal' },
    { value: 'vertical', name: 'Vertical' },
];

export const consumptionTypeOptions: FormRadioOption<
    keyof DeviceConsumptionType
>[] = [
    { value: 'bulk_storage' },
    { value: 'commercial_industrial' },
    { value: 'cooking' },
    { value: 'fireplace' },
    { value: 'generator' },
    { value: 'heating' },
    { value: 'hot_water' },
    { value: 'laundry_dryer' },
    { value: 'pool' },
    { value: 'retail_fill_up' },
    { value: 'backup_heating' },
];
